import { Scene2D, Container, PoolGroup } from '../odie';
import StateObserver, { user, now } from 'src/StateObserver';
import { PositionType } from '../lib/defs/types';
import Observer from '../lib/pattern/Observer';
import { BoosterId } from '../defs/booster';
import { GameEvent } from './defs/event';
import { ExtendedMapDef } from './defs/map';
import { SessionEntity } from './entities/SessionEntity';
import { BasicBlockSystem } from './systems/BasicBlockSystem';
import { CageBlockSystem } from './systems/CageBlockSystem';
import { ChainBlockSystem } from './systems/ChainBlockSystem';
import { ChameleonBlockSystem } from './systems/ChameleonBlockSystem';
import { CollisionSystem } from './systems/CollisionSystem';
import { DogBlockSystem } from './systems/DogBlockSystem';
import { NestBlockSystem } from './systems/NestBlockSystem';
import { MonitorSystem } from './systems/MonitorSystem';
import { PhaseSystem } from './systems/PhaseSystem';
import { PowerBlockSystem } from './systems/PowerBlockSystem';
import { RenderSystem } from './systems/RenderSystem';
import { SessionSystem } from './systems/SessionSystem';
import { SkullBlockSystem } from './systems/SkullBlockSystem';
import { SlimeBlockSystem } from './systems/SlimeBlockSystem';
import { SpiderBlockSystem } from './systems/SpiderBlockSystem';
import { waitAFrame } from '../lib/util/waitAFrame';
import { getPuzzleMap } from '../match2.assets';
import { app } from './app';
import { DartEffect } from './actions/effects/DartEffect';
import { IEffect } from './actions/effects/IEffect';
import { BulletEffect } from './actions/effects/BulletEffect';
import { DrillEffect } from './actions/effects/DrillEffect';
import { ShuffleEffect } from './actions/effects/ShuffleEffect';
import {
  GoalProgressAnalytics,
  Match2BoosterType,
  Match2PowerupType,
  Match2ScoringConfig,
  PuzzleResults,
  boosterNames,
  match2BoosterCatalog,
} from 'src/replicant/match2/match2.ruleset';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  getExtraMovesFromFreeContinue,
  getMatch2ContinueConfig,
  getMatch2TimedRewardRemainingTime,
  hasExtraMovesInInventory,
} from 'src/replicant/match2/match2.getters';
import { BlockId, blockPropsMap } from './defs/block';
import {
  blockIsBasic,
  blockIsPowerup,
  blockIsSpecial,
  blockIterateAll,
} from './util/blockTools';
import { match2GetTipId, TipId } from './defs/tips';
import { BlockEntity } from './entities/BlockEntity';
import { matchCheckTappable } from './util/matchTools';
import { GoalId } from './defs/goal';
import { gsap } from 'gsap';
import { ScoreSystem } from './systems/ScoreSystem';
import { PuzzleMapImporter } from './util/PuzzleMapImporter';
import { saveMatch2GameplayState } from '../savestate/match2-savestate.utils';
import { GoalCounts } from './components/MapComponent';
import { withLoading } from 'src/lib/utils';
import { CheatSystem } from './systems/CheatSystem';
import {
  getBlockGen,
  getUnlimitedBoosterUsedInSession,
  setUnlimitedBoosterUsedInSession,
} from '../match2.util';
import { BlockGenAlgorithm } from './defs/map';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import {
  getBoosterUsedAnalytics,
  getLeagueProps,
  getLevelVersioningProps,
  getPuzzleCostAnalytics,
  getSpendAnalytics,
  PowerUps,
} from '../match2.analytics';
import { CurtainBlockSystem } from './systems/CurtainBlockSystem';
import { isEditorMode } from '../editor/editor.utils';
import { ShellBlockSystem } from './systems/ShellBlockSystem';
import { PondBlockSystem } from './systems/PondBlockSystem';
import { trackPinchConversion } from 'src/lib/analytics/events';
import { SodaBlockSystem } from './systems/SodaBlockSystem';
import { MushroomBlockSystem } from './systems/MushroomBlockSystem';
import { AlarmBlockSystem } from './systems/AlarmBlockSystem';
import { BubblesBlockSystem } from './systems/BubblesBlockSystem';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { SaveStateData } from '../savestate/match2-savestate.data';
import { WallBlockSystem } from './systems/WallBlockSystem';
import { DespawnSystem } from './systems/DespawnSystem';
import { isSocialContinueAvailable } from 'src/replicant/social/social.getters';
import { getFriendsForInvite } from 'src/game/social/social.sequence';
import {
  maxSocialContinueRewardMoves,
  minFriendsForSocialContinue,
} from 'src/replicant/social/social.ruleset';
import { isStreakBattleRoyaleActive } from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { Feature } from 'src/replicant/analytics';
import {
  getAdContinueMoves,
  shouldOfferAdContinue,
} from 'src/game/ads/ads.util';
import { Stats } from 'src/game/stats';
import { getGameScene, setGameScene } from './getGameScene';
import { FirecrackerBoxBlockSystem } from './systems/FirecrackerBoxBlockSystem';
import { trackSessionStateSave } from 'src/game/crossplay/crossplay.tracking';
import { RainbowBubbleSystem } from './systems/RainbowBubbleSystem';
import { puzzleDefeatSanityCheck } from './util/puzzleSanityCheck';
import { captureGenericError } from 'src/lib/sentry';
import { track } from 'src/lib/analytics/utils';
import { GrassBlockSystem } from './blocks/grass/GrassBlockSystem';
import { getRaceAnalytics } from '../races/races.analytics';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
} from 'src/replicant/match2/leagues/leagues.getters';
import {
  getSuperLollipopActivationCount,
  isSuperLollipopActive,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { getTreasureExpressPuzzleEventProperties } from 'src/replicant/treasureExpress/treasureExpress.getters';
import { shouldApplyMoveReduction } from '../movereduction/movereduction.utils';
import {
  firstMovePinchRetrySequence,
  secondMovePinchRetrySequence,
} from '../match2.sequence';
import { TOS_PUZZLE_PINCH_REWARD_MOVES } from 'src/game/tosPuzzlePinch/tosPuzzlePinch.utils';
import { handleTosPuzzlePinchNudge } from 'src/game/tosPuzzlePinch/tosPuzzlePinch.sequence';
import { MagicBunniesBlockSystem } from './blocks/magicBunnies/MagicBunniesBlockSystem';
import { dreamDriftContinue } from 'src/game/dreamDrift/dreamDrift.sequence';
import { getDreamDriftPuzzleEventProperties } from 'src/replicant/dreamDrift/dreamDrift.analytics';
import { getExtraMovesOffer } from '../movesPinch/extraMovesOffer';

// types
//-----------------------------------------------------------------------------
/** Configuration used to create a Match-2 gameplay runtime. */
export interface Options {
  stage: Container;
  /** Whether to offer follow-up recovery after the initial continue offer. */
  enableLoseRecovery?: boolean;
}

type ContinueType = 'social' | 'free' | 'gems' | 'ad' | 'native' | 'iap_tos';

// constants
//-----------------------------------------------------------------------------
const boosterEffects: {
  [key in BoosterId]: {
    factory: (scene: GameScene, position: PositionType) => IEffect;
    assets: () => string[];
  };
} = {
  dart: {
    factory: (scene, position) => new DartEffect(scene, { position }),
    assets: DartEffect.assets,
  },
  bullet: {
    factory: (scene, position) => new BulletEffect(scene, { row: position.y }),
    assets: BulletEffect.assets,
  },
  drill: {
    factory: (scene, position) =>
      new DrillEffect(scene, { column: position.x }),
    assets: DrillEffect.assets,
  },
  roulette: {
    factory: (scene) => new ShuffleEffect(scene),
    assets: () => [],
  },
};

/*
    match2 puzzle game scene
*/
export class GameScene extends Scene2D {
  // fields
  //-------------------------------------------------------------------------
  // input
  public stage: Container;
  // public renderer: Renderer;
  // events
  public events = new Observer<GameEvent>();
  // state
  protected _active = false;
  // scene
  public basicBlockSystem!: BasicBlockSystem;
  public cageBlockSystem!: CageBlockSystem;
  public chameleonBlockSystem!: ChameleonBlockSystem;
  public firecrackerBoxBlockSystem!: FirecrackerBoxBlockSystem;
  public chainBlockSystem!: ChainBlockSystem;
  public pondBlockSystem!: PondBlockSystem;
  public collisionSystem!: CollisionSystem;
  public dogBlockSystem!: DogBlockSystem;
  public nestBlockSystem!: NestBlockSystem;
  public monitorSystem!: MonitorSystem;
  public phaseSystem!: PhaseSystem;
  public powerBlockSystem!: PowerBlockSystem;
  public renderSystem!: RenderSystem;
  public sessionSystem!: SessionSystem;
  public skullBlockSystem!: SkullBlockSystem;
  public alarmBlockSystem!: AlarmBlockSystem;
  public slimeBlockSystem!: SlimeBlockSystem;
  public mushroomBlockSystem!: MushroomBlockSystem;
  public spiderBlockSystem!: SpiderBlockSystem;
  public shellBlockSystem!: ShellBlockSystem;
  public bubblesBlockSystem!: BubblesBlockSystem;
  private curtainBlockSystem!: CurtainBlockSystem;
  public sodaBlockSystem!: SodaBlockSystem;
  public wallBlockSystem!: WallBlockSystem;
  public scoreSystem!: ScoreSystem;
  public despawnSystem!: DespawnSystem;
  public cheatSystem?: CheatSystem;

  // singletons
  public sessionEntity!: SessionEntity;

  public gameOver = false;
  private finished = false;
  /** Whether an external recovery flow aborted the current game. */
  public aborted = false;
  public moves = 0;
  public usedAutoShuffles = 0;
  public visualMoves = 0;
  public level = 0;
  public mapDef?: ExtendedMapDef;

  public usingSaveState = false;
  protected completePromise: Promise<void> | null = null;
  protected completePromiseResolve?: () => void;
  public selectedBooster?: BoosterId;

  public continues = 0;
  public extraMoves = 0;
  public adContinueMoves = 0;
  public usedBoosters: Partial<Record<Match2BoosterType, number>> = {};
  public blocksBroken: Partial<Record<BlockId, number>> = {};
  public powerBlocksCreated: Partial<Record<Match2PowerupType, number>> = {};
  public blocksBrokenAnalytics: Record<string, number> = {};

  public isApplyingExtraPowerups = false;
  public isBoosterTutorialActive = false;

  private goalCountsOverwrite?: GoalCounts;

  protected levelBroken: boolean = false;

  // For analytics
  public startingBoosters: Match2PowerupType[] = [];
  public startingBoostersAnalytics!: string;
  public dynamicBlockGenType?: BlockGenAlgorithm;
  public socialContinueMoves = 0;
  private continueSpend = 0;
  private boosterSpend = 0;
  private boostersPurchased: Partial<Record<BoosterId, number>> = {};
  private boosterSpendType: PuzzleResults['boosterSpendType'];
  /** Whether this puzzle uses the standard follow-up loss recovery offers. */
  private readonly enableLoseRecovery: boolean;
  /** Whether this runtime is playing a Dream Drift puzzle. */
  public isDreamDrift = false;

  // properties
  //-------------------------------------------------------------------------
  public get active(): boolean {
    return this._active;
  }

  public get scoringConfig(): Match2ScoringConfig {
    return this.scoreSystem.scoringConfig;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: Options) {
    super({ stage: options.stage });

    // set fields
    this.stage = options.stage;
    this.enableLoseRecovery = options.enableLoseRecovery ?? true;
    // this.renderer = options.renderer;

    // add systems in order of execution
    this.sessionSystem = this.addSystem(SessionSystem);
    this.basicBlockSystem = this.addSystem(BasicBlockSystem);
    this.dogBlockSystem = this.addSystem(DogBlockSystem);
    this.chameleonBlockSystem = this.addSystem(ChameleonBlockSystem);
    this.firecrackerBoxBlockSystem = this.addSystem(FirecrackerBoxBlockSystem);
    this.nestBlockSystem = this.addSystem(NestBlockSystem);
    this.skullBlockSystem = this.addSystem(SkullBlockSystem);
    this.alarmBlockSystem = this.addSystem(AlarmBlockSystem);
    this.slimeBlockSystem = this.addSystem(SlimeBlockSystem);
    this.mushroomBlockSystem = this.addSystem(MushroomBlockSystem);
    this.spiderBlockSystem = this.addSystem(SpiderBlockSystem);
    this.powerBlockSystem = this.addSystem(PowerBlockSystem);
    this.cageBlockSystem = this.addSystem(CageBlockSystem);
    this.chainBlockSystem = this.addSystem(ChainBlockSystem);
    this.pondBlockSystem = this.addSystem(PondBlockSystem);
    this.curtainBlockSystem = this.addSystem(CurtainBlockSystem);
    this.sodaBlockSystem = this.addSystem(SodaBlockSystem);
    this.wallBlockSystem = this.addSystem(WallBlockSystem);
    this.shellBlockSystem = this.addSystem(ShellBlockSystem);
    this.bubblesBlockSystem = this.addSystem(BubblesBlockSystem);
    this.collisionSystem = this.addSystem(CollisionSystem);
    this.phaseSystem = this.addSystem(PhaseSystem);
    this.renderSystem = this.addSystem(RenderSystem);
    this.monitorSystem = this.addSystem(MonitorSystem);
    this.scoreSystem = this.addSystem(ScoreSystem);
    this.despawnSystem = this.addSystem(DespawnSystem);
    this.addSystem(RainbowBubbleSystem);
    this.addSystem(GrassBlockSystem);
    this.addSystem(MagicBunniesBlockSystem);

    if (process.env.IS_DEVELOPMENT) {
      this.cheatSystem = this.addSystem(CheatSystem);
    }

    setGameScene(this);
    this.events.subscribe(this.onEvent.bind(this));
  }

  private onEvent(e?: GameEvent) {
    if (!e) return;
    if (e.id === 'move') {
      this.updateMoves();
    } else if (e.id === 'round') {
      this.updateRound();
    }
  }

  protected updateMoves() {
    if (this.moves > 0) this.moves -= 1;
  }

  public async updateRound() {
    // Puzzle already complete, bail.
    if (this.gameOver) return;

    // Get attempts stats
    const attemptsStats = Stats.api.getPuzzleAttemptsStats(user());

    // Technically this should never happen as the puzzle should have been shuffled to avoid the unplayable situation
    // This check should only happen during input phase, where the player can make a move
    if (this.phaseSystem.phase === 'input' && !matchCheckTappable(this)) {
      const movesLimit = this.mapDef?.moves || 0;
      const movesUsed = movesLimit - this.moves + this.extraMoves;

      track('PuzzleBroken', {
        level: this.level,
        movesLeft: this.moves,
        movesUsed,
        startingBoosters: this.startingBoostersAnalytics as PowerUps,
        buruStreakValue: user().match2.buruStreak.value,
        usingSaveState: this.usingSaveState,
        isStreakQuestActive: isStreakBattleRoyaleActive(user(), now()),
        ...getLevelVersioningProps(this.level, this.mapDef),
        ...attemptsStats,
      });

      // Broken level detection
      this.levelBroken = true;
      this.finish(); // Restart level, see: startMatch2() sequence

      return;
    }

    // Check if goals were cleared. If so, handle victory.
    const remainingGoals = this.getRemainingGoals();
    if (remainingGoals === 0) {
      this.gameOver = true;
      await this.scoreSystem.handleVictory();
      this.finish();
      return;
    }

    // User has moves left, bail.
    if (this.moves > 0) return;

    // Analyze the defeat state and log events if something seems off - try/catch just to be extra safe
    try {
      puzzleDefeatSanityCheck(this);
    } catch (e) {
      captureGenericError('PuzzleDefeatSanityCheckError', e);
    }

    // Gameover, no more moves.
    this.gameOver = true;

    const puzzleFinishData = this.getPuzzleResults();
    const { price } = getMatch2ContinueConfig(StateObserver.getState().user);

    track('PuzzlePinch', {
      outOfMovesCount: this.continues + 1,
      level: this.level,
      startingBoosters: this.startingBoostersAnalytics as PowerUps,
      buruStreakValue: user().match2.buruStreak.value,
      usingSaveState: this.usingSaveState,
      isStreakQuestActive: isStreakBattleRoyaleActive(user(), now()),
      rubberbandingStep: user().match2.winLadderStep,
      blockGenType: getBlockGen(this.mapDef!, this.level),
      dynamicBlockGenType: this.dynamicBlockGenType,
      percentScore: puzzleFinishData.score?.percentage,
      totalObstaclesRemaining: puzzleFinishData.totalObstaclesRemaining,
      ratioObstaclesRemaining:
        puzzleFinishData.totalObstaclesRemaining /
        puzzleFinishData.totalObstacleGoals,
      remainingMoves: 0,
      limitMoves: puzzleFinishData.moves.limit,
      socialContinueMoves: puzzleFinishData.socialContinueMoves,
      losingStreak: user().match2.losingStreak,
      isSuperLollipopActive: isSuperLollipopActive(user()),
      superLollipopStreakID: isSuperLollipopActive(user())
        ? getSuperLollipopActivationCount(user())
        : undefined,
      isReducedMoves: shouldApplyMoveReduction(),
      wasSuperLollipopLostThisLevel:
        !!user().superLollipop?.lostSuperLollipopThisLevel,

      ...getLevelVersioningProps(this.level, this.mapDef),
      ...getLeagueProps(user()),
      ...attemptsStats,
      ...this.blocksBrokenAnalytics,
      ...this.getGoalAnalytics(),
      ...getBoosterUsedAnalytics(puzzleFinishData),
      ...getPuzzleCostAnalytics(this.startingBoosters, puzzleFinishData),
      ...getSpendAnalytics(puzzleFinishData),
      ...getRaceAnalytics(),
      ...getTreasureExpressPuzzleEventProperties(user(), now()),
      ...(this.isDreamDrift ? getDreamDriftPuzzleEventProperties(user()) : {}),
    });

    app.sound.play('puzzle-lose-new', {});

    // Only allow extra moves if not in editor mode
    if (!isEditorMode()) {
      // Ran out of moves, offer to buy extra.
      const boughtExtraMoves = await this.buyExtraMoves();
      if (this.finished) return;

      if (boughtExtraMoves) {
        this.gameOver = false;
        return;
      }
    }

    // User did not buy extra moves, handle defeat.
    this.finish();
  }

  /**
   * Offer and process the available out-of-moves continue options.
   *
   * @returns Whether the player successfully continued the puzzle.
   */
  private async buyExtraMoves() {
    // Stop continue handling if another flow already finished the scene.
    if (this.finished) return false;

    // Dream Drift bypasses inventory continues for its gem-only sequence.
    const useFreeContinue =
      !this.isDreamDrift && hasExtraMovesInInventory(user());

    // Allow the use of any available free continues
    if (useFreeContinue) {
      const inventory = user().match2.inventory.extraMoves;
      const moves = getExtraMovesFromFreeContinue(user());
      const result = await openPopupPromise('popupMatch2FreeContinue', {
        inventory,
        moves,
      });

      if (this.finished) return false;

      if (result) {
        await StateObserver.invoke.consumeFreeContinue();
        this.triggerContinue('free', moves, 0);
      }

      return result;
    }

    await withLoading(async () => {
      // This looks a bit weird, but it's because the game actually waits 3 frames to fully start up, due to some
      // framework issue (see startGame function). So we need to wait 5 frames to make sure the game is fully started before saving
      await waitAFrame(5);

      // Preserve the zero-move board before a continue route backgrounds the app.
      try {
        await saveMatch2GameplayState('outOfMoves');
      } catch (error) {
        // Report the failed save and allow the player to choose a continue route.
        captureGenericError('Match2OutOfMovesSaveStateError', error);
        return;
      }

      // Track only successfully persisted out-of-moves save states.
      trackSessionStateSave({
        feature: 'puzzle_continue',
        subFeature: 'puzzle_continue',
        timestamp: now(),
        origin: 'outOfMoves',
        continueCount: this.continues,
      });
    });

    const checkEnoughFriends = async () => {
      const friends = await getFriendsForInvite();
      return friends.length >= minFriendsForSocialContinue;
    };

    let success = false;
    const playerState = StateObserver.getState().user;
    const extraMoves = this.isDreamDrift
      ? 0
      : getExtraMovesOffer(playerState, this.continues);
    const adContinue =
      !this.isDreamDrift &&
      shouldOfferAdContinue(playerState, this.adContinueMoves);

    if (adContinue) {
      const result = await openPopupPromise('popupAdContinue', {
        extraMoves,
      });
      // It can happen on desync error
      if (!result) return false;
      if (this.finished) return false;

      if (result.watchedAd) {
        // Every 6 hours players can do at most 3 free continues (upto 9 ads shows minimum 3 ads shows)
        await StateObserver.invoke.consumeAd();

        const result = await openPopupPromise('popupAdContinueStreak', {});
        if (this.finished) return false;

        let moves = 0;
        for (let i = 0; i < result?.watchedAds; i++) {
          moves += getAdContinueMoves(i);
        }

        this.adContinueMoves += moves;
        this.triggerContinue('ad', moves, 0);
        return true;
      }

      success = !!result.purchase;
    }

    const socialContinue =
      !this.isDreamDrift &&
      !adContinue &&
      isSocialContinueAvailable(user(), now()) &&
      (await withLoading(checkEnoughFriends, {
        feature: Feature.MATCH2._,
        subFeature: Feature.MATCH2.CHECK_SOCIAL_CONTINUE_AVAILABLE,
      }));

    /**
     * If social continue is available and there are enough friends for invite
     * to cover the max moves reward, show the social continue popup.
     *
     * if skipped, continue to the usual moves purchase flow.
     */
    if (socialContinue) {
      const result = await openPopupPromise('popupSocialContinue', {
        extraMoves,
      });
      // It can happen on desync error
      if (!result) return false;
      if (this.finished) return false;

      const friendsInvited = result.invites ?? 0;
      const moves = Math.min(friendsInvited, maxSocialContinueRewardMoves);

      if (moves > 0) {
        // Trigger a cooldown for the social continue
        await StateObserver.invoke.setMatch2SocialContinueCooldownTimestamp();

        // Tracks the number of moves gained from the social continue
        this.socialContinueMoves += moves;
        this.triggerContinue('social', moves, 0);
        return true;
      }

      success = !!result.purchase;
    }

    const { moves, price } = getMatch2ContinueConfig(
      StateObserver.getState().user,
    );
    const purchaseContinue = async () => {
      if (this.finished) {
        captureGenericError('Match2ContinueAfterFinish', null, {
          extra: {
            puzzleLevel: this.level,
            continues: this.continues,
            price,
          },
        });
        return;
      }

      const { powerups } = getMatch2ContinueConfig(
        StateObserver.getState().user,
      );
      await StateObserver.invoke.continueMatch2Puzzle();

      if (powerups) {
        const extracted: Match2PowerupType[] = [];
        powerups.forEach((powerup) =>
          extracted.push(...new Array(powerup.amount).fill(powerup.type)),
        );
        // Queue and apply complimentary powerups from the extra moves purchased
        this.powerBlockSystem.queueExtraPowerups(extracted, true);
        // Ensure that the board is shuffled after applying the extra moves.
        this.phaseSystem.phase = 'shuffle';
      }

      this.triggerContinue('gems', moves + extraMoves, price);
    };

    // Dream Drift replaces every normal gem recovery route with its custom popup.
    if (this.isDreamDrift) {
      const continued = await dreamDriftContinue(price);

      // Closing the custom popup immediately ends the Dream Drift attempt.
      if (!continued) return false;

      // Apply the shared Match-2 gem spend, moves, and continue powerups.
      await purchaseContinue();
      return !this.finished;
    }

    const tosPuzzlePinchResult = await handleTosPuzzlePinchNudge({
      level: this.level,
      moves: moves + extraMoves,
      price,
      continues: StateObserver.getState().user.match2.continues,
      pinchAnalytics: this.getPinchAnalyticsData(),
      levelVersioningProps: getLevelVersioningProps(this.level, this.mapDef),
      continueViaTos: async () => {
        if (this.finished) {
          captureGenericError('Match2ContinueAfterFinish', null, {
            extra: {
              puzzleLevel: this.level,
              continues: this.continues,
              type: 'iap_tos',
              price: 0,
            },
          });
          return;
        }

        await StateObserver.invoke.continueMatch2PuzzleViaTos();
        this.triggerContinue('iap_tos', TOS_PUZZLE_PINCH_REWARD_MOVES, 0);
      },
      purchaseContinue,
    });
    const hasConsumedFirstPinch = tosPuzzlePinchResult.consumedFirstPinch;

    if (tosPuzzlePinchResult.handled) {
      return tosPuzzlePinchResult.success && !this.finished;
    }

    // 1st pinch
    // Purchase is built in the social and ad continue popups
    if (!success && !socialContinue && !adContinue && !hasConsumedFirstPinch) {
      success = await openPopupPromise('popupMatch2MovePinch', {
        amount: moves,
        extraMoves,
        continues: StateObserver.getState().user.match2.continues,
        price,
        origin: 'pinch',
      });
    }

    // Offer the standard follow-up recovery only for regular Match-2 puzzles.
    if (!success && this.enableLoseRecovery) {
      success = await firstMovePinchRetrySequence(price);

      // 3rd Pinch (Retry 2)
      if (!success) {
        success = await secondMovePinchRetrySequence(price);
      }
    }

    if (success) {
      await purchaseContinue();
    }

    return success && !this.finished;
  }

  private triggerContinue(type: ContinueType, moves: number, price: number) {
    if (this.finished) {
      captureGenericError('Match2TriggerContinueAfterFinish', null, {
        extra: {
          puzzleLevel: this.level,
          continues: this.continues,
          type,
          moves,
          price,
        },
      });
      return;
    }

    this.moves = moves;
    this.extraMoves += moves;
    this.continues++;
    this.events.publish({ id: 'continue' });
    this.continueSpend += price;
    this.updateRound();

    // Get attempts stats
    const attemptsStats = Stats.api.getPuzzleAttemptsStats(user());

    // Track analytics event
    const subFeature =
      type === 'iap_tos' ? Feature.CONTINUE.TOS : (`continue_${type}` as const);

    track('PuzzleContinue', {
      feature: Feature.CONTINUE._,
      subFeature,
      continueType: type,
      step: this.continues,
      outOfMovesCount: this.continues, // Required for 'goalsAnalyticsProperties', but is essentially the same as step property above,
      totalGemsCosumed: price,
      matchLevel: this.level,
      usingSaveState: this.usingSaveState,
      addedMovesCount: moves,
      isSuperLollipopActive: isSuperLollipopActive(user()),
      superLollipopStreakID: isSuperLollipopActive(user())
        ? getSuperLollipopActivationCount(user())
        : undefined,
      blockGenType: getBlockGen(this.mapDef!, this.level),
      dynamicBlockGenType: this.dynamicBlockGenType,
      isReducedMoves: shouldApplyMoveReduction(),
      wasSuperLollipopLostThisLevel:
        !!user().superLollipop?.lostSuperLollipopThisLevel,
      ...this.getPinchAnalyticsData(),
      ...getLevelVersioningProps(this.level, this.mapDef),
      ...getLeagueProps(user()),
      isStreakQuestActive: isStreakBattleRoyaleActive(user(), now()),
      ...attemptsStats,
      ...getTreasureExpressPuzzleEventProperties(user(), now()),
      ...this.getGoalAnalytics(),
      ...(this.isDreamDrift ? getDreamDriftPuzzleEventProperties(user()) : {}),
    });

    trackPinchConversion({
      action: 'in_game',
      feature: Feature.CONTINUE._,
      subFeature,
      resource: 'moves',
    });
  }

  public getRemainingGoals() {
    const goals = this.sessionEntity.c.map.goals;
    return Object.values(goals).reduce(
      (total, remaining) => total + remaining,
      0,
    );
  }

  public getRemainingObstacles() {
    let remaining = 0;
    let key: GoalId;
    const goals = this.sessionEntity.c.map.goals;

    for (key in goals) {
      if (!this.isGoalAnObstacle(key)) continue;
      remaining += goals[key] ?? 0;
    }

    return remaining;
  }

  public getTotalObstacleGoals() {
    let total = 0;
    let key: GoalId;
    const goals = this.sessionEntity.c.map.mapDef.goals;

    for (key in goals) {
      if (!this.isGoalAnObstacle(key)) continue;
      total += goals[key] ?? 0;
    }

    return total;
  }

  public getGoalAnalytics(): GoalProgressAnalytics {
    let key: GoalId;
    let i = 0;
    let total = 0;
    const remaining = this.sessionEntity.c.map.goals;
    const goals = this.sessionEntity.c.map.mapDef.goals;
    const analytics: any = {};

    for (key in remaining) {
      i++;

      const progress = 1 - (remaining[key] ?? 0) / (goals[key] ?? 0);

      analytics[`goalProgress${i}`] = progress;
      analytics[`goalProgress${i}Type`] = key;

      total += progress;
    }

    analytics.goalProgressAverge = total / Math.max(i, 1);

    return analytics as GoalProgressAnalytics;
  }

  private isGoalAnObstacle(id: GoalId) {
    return !(
      id.includes('basic') ||
      id.includes('rocket') ||
      id.includes('bomb') ||
      id.includes('cube')
    );
  }

  public getResult() {
    if (this.levelBroken) return 'broken';

    const remainingGoals = this.getRemainingGoals();
    if (remainingGoals === 0 || this.moves === 0) {
      if (remainingGoals === 0) {
        return 'win';
      } else {
        return 'lose';
      }
    }
    return 'quit';
  }

  /**
   * Finish gameplay and continue through the normal post-game sequence.
   *
   * @returns Nothing.
   */
  public finish() {
    // Avoid double-finishing (can happen due to delayed ticks / multiple callers).
    if (this.finished) return;

    // Complete gameplay without marking it as externally aborted.
    this.completeGameplay();
  }

  /**
   * Abort gameplay without recording a puzzle result.
   *
   * @returns Nothing.
   */
  public abort() {
    // A completed game has already entered its normal post-game sequence.
    if (this.finished) return;

    // Mark the game as aborted before waking the Match2 sequence.
    this.aborted = true;

    // Complete the scene lifecycle so recovery can replace the active screen.
    this.completeGameplay();
  }

  /**
   * Resolve gameplay and disable further interaction with the scene.
   *
   * @returns Nothing.
   */
  private completeGameplay() {
    // Mark the game as finished so delayed interactions cannot mutate it.
    this.finished = true;

    // Fully disable gameplay interaction on finish
    this.stage.view.updateOpts({
      blockEvents: true,
    });

    // Dispatch a finish event, so any UI from outside the game can be updated too
    this.events.publish({ id: 'finish' });

    // Wake any sequence waiting for gameplay to finish.
    if (this.completePromiseResolve) {
      this.completePromiseResolve();
      this.completePromiseResolve = undefined;
    }
  }

  /**
   * Wait until gameplay finishes or is externally aborted.
   *
   * @returns Completion promise, or null before gameplay starts.
   */
  public waitForComplete() {
    return this.completePromise;
  }

  public getPuzzleResults(): PuzzleResults {
    if (!this.mapDef) throw new Error('No map def set');
    const movesLimit = this.mapDef?.moves || 0;
    const movesUsed = movesLimit - this.moves + this.extraMoves;
    const result = this.getResult();

    return {
      result,
      moves: {
        limit: movesLimit,
        used: movesUsed,
        remaining: Math.max(0, this.moves),
      },
      usingSaveState: this.usingSaveState,
      usedContinues: this.continues,
      socialContinueMoves: this.socialContinueMoves,
      adContinueMoves: this.adContinueMoves,
      continueSpend: this.continueSpend,
      usedBoosters: this.usedBoosters,
      boosterSpend: this.boosterSpend,
      boosterSpendType: this.boosterSpendType,
      usedAutoShuffles: this.usedAutoShuffles,
      blocksBroken: this.blocksBroken,
      powerBlocksCreated: this.powerBlocksCreated,
      blocksBrokenAnalytics: this.blocksBrokenAnalytics,
      totalObstacleGoals: this.getTotalObstacleGoals(),
      totalObstaclesRemaining: this.getRemainingObstacles(),
      ...(result === 'win'
        ? {
            score: {
              points: this.scoreSystem.score,
              stars: this.scoreSystem.stars,
              percentage: this.scoreSystem.score / this.scoreSystem.baseScore,
            },
          }
        : { goalProgressAnalytics: this.getGoalAnalytics() }),
    };
  }

  // impl
  //-------------------------------------------------------------------------
  public override resize(w: number, h: number): void {
    super.resize(w, h);
  }

  // api
  //-------------------------------------------------------------------------
  public async startGame(level: number, saveState?: SaveStateData) {
    this.completePromise = new Promise((resolve) => {
      this.completePromiseResolve = resolve;
    });

    // Start game's internal app ticker
    app.start();

    this.level = level;
    this.gameOver = false;
    this.finished = false;
    this.aborted = false;
    this.levelBroken = false;
    this.continues = 0;
    this.extraMoves = 0;
    this.socialContinueMoves = 0;
    this.adContinueMoves = 0;
    this.usedAutoShuffles = 0;
    this.usedBoosters = {};
    this.blocksBroken = {};
    this.powerBlocksCreated = {};
    this.blocksBrokenAnalytics = {};

    // set map
    this.mapDef = await getPuzzleMap(level);

    // Debug rubberbanding
    if (this.cheatSystem) {
      const blockGen = getBlockGen(this.mapDef, level);
      this.cheatSystem.setStep(user().match2.winLadderStep);
      this.cheatSystem.setStartAlgorithm(blockGen);
    }

    if (!this.mapDef) {
      throw new Error(`[Match2] No map found for level ${level}`);
    }

    this.moves = this.mapDef.moves;

    if (saveState) {
      // Update the game with the save state data
      this.setSaveState(saveState);
    }

    console.log('[Match2] Start game', this.mapDef);
    this.sessionSystem.setMap(this.mapDef);

    // start
    super.start();

    //TODO: shouldnt need this. but apparently pixi issue.
    // wait 3 frames to allow all systems to have started and stepped first frame
    await waitAFrame(3);

    if (this.goalCountsOverwrite) {
      // Inject saved goal counts into map component if using save state
      this.sessionEntity.c.map.goals = { ...this.goalCountsOverwrite };
    }

    // set active
    this._active = true;

    this.onGameStart();
  }

  // Dispatch onGameStart signal to all blocks
  protected onGameStart() {
    this.sessionEntity.c.state.onGameStart();
    blockIterateAll(this, (base, overlay) => {
      base?.c.block?.signals?.onGameStart.emit();
      overlay?.c.block?.signals?.onGameStart.emit();
    });
  }

  private setSaveState(saveState: SaveStateData) {
    // Skipt if mapDef is not defined
    if (!this.mapDef) return;

    // Skip if data level is not the same as current level
    if (saveState.level !== this.level) return;

    // Decode mapDef object from save state
    const saveStateMapDef = new PuzzleMapImporter(saveState.mapDef).import();

    // Skip if map def could not be decoded properly or if mapDef is not set
    if (!saveStateMapDef) {
      console.error('Invalid save state data');
      return;
    }

    // Save state is valid, set usingSaveState flag
    this.usingSaveState = true;
    this.moves = saveState.moves;
    this.mapDef.grid = saveStateMapDef.grid;
    this.visualMoves = saveState.visualMoves;
    this.scoreSystem.setScore(saveState.score ?? 0, saveState.scoringConfig);

    // This will get injected into the map component later
    this.goalCountsOverwrite = saveState.goalCounts;

    // Analytics properties
    this.continues = saveState.continues;
    this.extraMoves = saveState.extraMoves;
    this.socialContinueMoves = saveState.socialContinueMoves;
    this.adContinueMoves = saveState.adContinueMoves ?? 0;
    this.usedBoosters = saveState.usedBoosters;
    this.blocksBroken = saveState.blocksBroken;
    this.powerBlocksCreated = saveState.powerBlocksCreated;
    this.blocksBrokenAnalytics = saveState.blocksBrokenAnalytics;

    // Clear powerup queue.
    this.powerBlockSystem.clearExtraPowerups();
  }

  public destroy(): void {
    // Kill all existing tweens, to prevent queued actions from running after stopped
    gsap.globalTimeline.clear();

    // Remove all event listeners
    this.events.unsubscribeAll();

    // Stop game's internal app ticker
    app.stop();

    // reset active
    this._active = false;

    // Destroy the scene
    super.destroy();

    // Destroy the stage container
    this.stage.destroy();

    // Clear all pools
    PoolGroup.clearAll();

    // Clear the global reference only if this scene still owns it.
    if (getGameScene() === this) setGameScene(undefined);
  }

  public purchasedBooster(boosterId: BoosterId) {
    this.boosterSpend += match2BoosterCatalog[boosterId].price;
    this.boostersPurchased[boosterId] = this.boostersPurchased[boosterId] ?? 0;
    this.boostersPurchased[boosterId]!++;
  }

  public async playBooster(boosterId: BoosterId) {
    if (this.selectedBooster === boosterId) {
      this.cancelBooster();
      return;
    }

    this.cancelBooster();
    console.log('[Match2] Play booster:', boosterId);
    this.selectedBooster = boosterId;

    let handPosition;

    if (this.isBoosterTutorialActive) {
      handPosition = this.getBoosterTutorialHandPosition();
    }

    this.events.publish({
      id: 'booster',
      boosterId: this.selectedBooster,
      subtype: 'activate',
      handPosition,
    });
  }

  private getBoosterTutorialHandPosition() {
    const blocks: BlockEntity[] = [];

    // iterate blocks
    blockIterateAll(this, (base, overlay) => {
      const entity = overlay || base;
      const id = entity.c.block.blockId;
      const isImmortal = blockPropsMap[entity.c.block.blockId].immortal;

      if (!isImmortal && !id.includes('panther')) blocks.push(overlay || base);
    });

    const pick = getRandomItemFromArray(blocks, Math.random);

    if (pick) {
      const { x, y } = pick.c.position.mapPosition;
      return { x, y };
    }

    // A fallback that should never happen.
    return { x: 0, y: 0 };
  }

  public async cancelBooster() {
    if (!this.selectedBooster) return;
    console.log('[Match2] Cancel booster:', this.selectedBooster);
    this.events.publish({
      id: 'booster',
      boosterId: this.selectedBooster as BoosterId,
      subtype: 'cancel',
    });
    this.selectedBooster = undefined;
  }

  public async runBooster(boosterId: BoosterId, position: PositionType) {
    // Only allow boosters to run on enabled (not empty) cells
    const cell = this.sessionEntity.c.map.getCellAt(position);
    if (!cell?.enabled) return;

    // No consumption for booster tutorial mode
    if (this.isBoosterTutorialActive) {
      this.isBoosterTutorialActive = false;
    } else {
      const isUnlimited = getMatch2TimedRewardRemainingTime(
        user(),
        `boosters-${boosterId}`,
        now(),
      );
      const unlimitedUsed = getUnlimitedBoosterUsedInSession(boosterId);

      if (!isUnlimited || unlimitedUsed) {
        await StateObserver.invoke.consumeMatch2Booster({ type: boosterId });

        const consumingPurchased = !!this.boostersPurchased[boosterId];

        if (consumingPurchased) {
          this.boosterSpendType =
            this.boosterSpendType === 'Free'
              ? 'Mixed'
              : (this.boosterSpendType ?? 'Paid');
        } else {
          this.boosterSpendType =
            this.boosterSpendType === 'Paid'
              ? 'Mixed'
              : (this.boosterSpendType ?? 'Free');
        }
      }

      if (isUnlimited && !unlimitedUsed) {
        setUnlimitedBoosterUsedInSession(boosterId);
      }
    }

    this.events.publish({
      id: 'booster',
      boosterId: this.selectedBooster as BoosterId,
      subtype: 'run',
    });

    console.log('[Match2] Run booster:', boosterId);
    const phase = this.sessionEntity.c.phase;

    // notify
    this.events.publish({ id: 'booster', boosterId });

    // push activations
    phase.activePush();

    // Track analytics event
    const leaguePhase = canPlayLeague(user())
      ? getCurrentLeaguePhase(user())
      : undefined;

    track('BoosterUsed', {
      booster: boosterNames[boosterId],
      ...this.getPinchAnalyticsData(),
      puzzleLevel: this.level,
      leaguePhase,
    });

    track('BoosterConsume', {
      boosterName: boosterNames[boosterId],
      feature: Feature.MATCH2.BOOSTER_SELECT,
      ...this.getPinchAnalyticsData(),
      puzzleLevel: this.level,
      leaguePhase,
    });

    // execute booster effect
    await boosterEffects[boosterId].factory(this, position).execute();

    // pop activations
    await phase.activePop();

    this.events.publish({
      id: 'booster',
      boosterId: this.selectedBooster as BoosterId,
      subtype: 'finish',
    });

    this.selectedBooster = undefined;

    // Add to results
    this.usedBoosters[boosterId] = (this.usedBoosters[boosterId] ?? 0) + 1;
  }

  public findBlocksWithTips() {
    const blocks: { block: BlockEntity; tipId: TipId }[] = [];

    // iterate blocks
    blockIterateAll(this, (base, overlay) => {
      // prioritize overlay entity
      const entity = overlay || base;

      // get tip id for block
      const tipId = match2GetTipId(entity.c.block.blockId);
      // skip if tip not available
      if (!tipId) return;

      // skip if already queued
      const alreadyAdded = blocks.find((block) => block.tipId === tipId);
      if (alreadyAdded) return;

      // show tip
      blocks.push({ block: entity, tipId });
    });

    return blocks.map(({ block, tipId }) => {
      return {
        blockId: block.c.block.blockId,
        tipId,
        view: block.c.view2d.view.view,
      };
    });
  }

  public recordBlockBroken(blockId: BlockId) {
    // Do not record block broken data if the game is over.
    if (this.gameOver) return;

    // Record raw block broken data
    this.blocksBroken[blockId] = (this.blocksBroken[blockId] ?? 0) + 1;

    // Record mapped block broken analytics data
    const nameMap: Partial<Record<BlockId, string>> = {
      basicBlue: 'blueBroken',
      basicRed: 'redBroken',
      basicGreen: 'greenBroken',
      basicYellow: 'yellowBroken',
      basicPurple: 'purpleBroken',
      rocketHorizontal: 'rocketBroken',
      rocketVertical: 'rocketBroken',
      bomb: 'bombBroken',
      cubeBlue: 'cubeBroken',
      cubeRed: 'cubeBroken',
      cubeGreen: 'cubeBroken',
      cubeYellow: 'cubeBroken',
      cubePurple: 'cubeBroken',
    };
    const name = nameMap[blockId];
    if (!name) return;
    this.blocksBrokenAnalytics[name] =
      (this.blocksBrokenAnalytics[name] ?? 0) + 1;
  }

  public recordPowerBlockCreated(type: Match2PowerupType) {
    this.powerBlocksCreated[type] = (this.powerBlocksCreated[type] ?? 0) + 1;
  }

  public getPinchAnalyticsData() {
    return {
      score: this.scoreSystem.score,
      percentScore: this.scoreSystem.score / this.scoreSystem.baseScore,
      totalObstaclesRemaining: this.getRemainingObstacles(),
      ratioObstaclesRemaining:
        this.getRemainingObstacles() / this.getTotalObstacleGoals(),
      remainingMoves: this.moves,
      limitMoves: this.mapDef?.moves,
    };
  }

  public getFrenzyObjectivesCount() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const config = getMatch2FrenzyEventConfig(user, now);
    let objectivesCount = 0;

    config?.objectives.forEach((id: BlockId) => {
      if (this.blocksBroken[id]) objectivesCount += this.blocksBroken[id] ?? 0;
    });

    return objectivesCount;
  }

  /**
   * Return the list of basic blocks available for the level, excluding any special blocks
   */
  public getSpawnListBasic() {
    const allSpawns = this.mapDef?.spawns?.slice() ?? [];
    // Filter out special blocks fron the spawn list
    return allSpawns.filter((id) => blockIsBasic(id));
  }

  /**
   * Return the list of special blocks available for the level, if there are remaining goals for them
   */
  public getSpawnListSpecial() {
    const allSpawns = this.mapDef?.spawns?.slice() ?? [];
    // Filter out basic blocks fron the spawn list
    const specialSpawns = allSpawns.filter((id) => blockIsSpecial(id));
    // Filter out special blocks that don't have goals remaining
    return specialSpawns.filter(
      (id) => this.countBlocks(id) < this.getGoalCount(id),
    );
  }

  /**
   * Return the list of powerup blocks available for the level
   */
  public getSpawnListPowerup() {
    const allSpawns = this.mapDef?.spawns?.slice() ?? [];
    // Filter powerup blocks from all spawns
    return allSpawns.filter((id) => blockIsPowerup(id));
  }

  /**
   * Return a list of all blocks (basic + special) available for spawning
   */
  public getSpawnList() {
    return [...this.getSpawnListBasic(), ...this.getSpawnListSpecial()];
  }

  /**
   * Get remaining goal count - will return 0 if goal is not defined
   */
  public getGoalCount(id: GoalId) {
    const props = blockPropsMap[id];
    // Use custom goal instead, if defined
    if (props?.customGoal) id = props.customGoal;
    const goals = this.sessionEntity.c.map.goals;
    return goals?.[id] ?? 0;
  }

  /**
   * Set remaining goal count - will create a new goal if it doesn't exist
   */
  public setGoalCount(id: GoalId, value: number) {
    const goals = this.sessionEntity.c.map.goals;
    goals[id] = value;
  }

  /**
   * Count existing blocks of a given type
   */
  public countBlocks(id: BlockId) {
    let count = 0;
    blockIterateAll(this, (base, overlay) => {
      const props = blockPropsMap[id];
      const customGoalId = props?.customGoal;
      if (customGoalId) {
        if (base.c.block.blockId === customGoalId) count++;
        if (overlay?.c.block.blockId === customGoalId) count++;
      }
      if (base.c.block.blockId === id) count++;
      if (overlay?.c.block.blockId === id) count++;
    });
    return count;
  }

  /**
   * Get the total number of moves defined in the level (excludes boost moves).
   */
  public getMovesLimit() {
    return this.mapDef?.moves || 0;
  }

  /**
   * Get current game round number based on moves remaining. First round is 1.
   */
  public getRound() {
    return this.getMovesLimit() - this.moves + 1;
  }

  /**
   * Count number of basic blocks on the board
   */
  public countBasicBlocks() {
    let count = 0;
    blockIterateAll(this, (base) => {
      // Basic blocks can only be the base blocks on each tile
      if (base.c.block.props.type === 'basic') {
        count += 1;
      }
    });
    return count;
  }

  /**
   * Get the total number of powerup blocks on the board
   * @returns
   */
  public countPowerupBlocks() {
    let count = 0;
    blockIterateAll(this, (base) => {
      // Basic blocks can only be the base blocks on each tile
      if (blockIsPowerup(base.c.block.blockId)) {
        count += 1;
      }
    });
    return count;
  }
}
